import FloorPlanEditor from './FloorPlanEditor.js';
import FloorPlan3D from './FloorPlan3D.js';
import { createFloorPlanProject } from './shared/floorplan.js';
import { useEffect, useRef, useState } from 'react';

import { authenticatedFetch } from './auth.js';
import { dataUrlToFile, fetchPrivateFile, fetchPrivateFileDataUrl } from './image-transfer.js';
import { clampPreviewPan, getContainedImageRect, mapPreviewPointToImage, type FloorPlanPreviewPan, type FloorPlanPreviewRect } from './floorplan-preview.js';
import {
  createFloorPlanLabelDocument,
  DEFAULT_FLOORPLAN_LABEL_STYLE,
  getFloorPlanLabelFontSizes,
  normalizeFloorPlanArea,
  normalizeFloorPlanLabelDocument,
  normalizeFloorPlanRotation,
  type FloorPlanLabel,
  type FloorPlanLabelStyle,
} from './floorplan-labels.js';
import { MAX_FLOORPLAN_CORRECTION_LENGTH, type FloorPlanCropRegion, type FloorPlanSurfaceMode } from './shared/floorplan.js';
import { GlobalWorkerOptions, getDocument, type PDFDocumentProxy } from 'pdfjs-dist/build/pdf.mjs';

type FloorPlanView = '2d' | '3d';
type FloorPlanPreviewMode = 'result' | 'original' | 'compare';

interface PreviewDragState {
  startX: number;
  startY: number;
  startPan: FloorPlanPreviewPan;
  pointerId: number;
}

interface FloorPlanResult {
  versionId?: string;
  sourceFileId?: string;
  sourceFilename?: string;
  degrees?: number;
  revision?: number;
  image: string;
  originalImage: string;
  imageUrl?: string;
  originalImageUrl?: string;
  downloadUrl?: string;
  originalDownloadUrl?: string;
  correctedImage?: string;
  mode: 'floorplan-2d' | 'floorplan-3d';
  promptSummary: string;
  original: { width: number; height: number };
  result: { width: number; height: number };
  note: string;
  floorTreatment?: FloorPlanSurfaceMode;
  resultFileId?: string;
  processingVersion?: string;
  downloadFilename?: string;
  distortion?: {
    kind: 'none' | 'rotation' | 'perspective' | 'local-fold' | 'uncertain';
    confidence: 'high' | 'medium' | 'low';
    corrected: boolean;
    reviewRequired: boolean;
    summary: string;
    details: string[];
  };
}

interface ApiErrorPayload {
  error?: { message?: string };
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const MAX_PDF_BYTES = 20 * 1024 * 1024;
const MAX_PDF_PAGES = 30;
const MAX_PDF_RENDER_EDGE = 2400;
const MAX_PDF_RENDER_PIXELS = 24_000_000;
GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.mjs';

function clampUnit(value: number): number { return Math.max(0, Math.min(1, value)); }

function getStagePoint(event: React.MouseEvent<HTMLDivElement>, stage: HTMLDivElement) {
  const bounds = stage.getBoundingClientRect();
  return {
    x: clampUnit((event.clientX - bounds.left) / bounds.width),
    y: clampUnit((event.clientY - bounds.top) / bounds.height),
  };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Das Grundrissbild konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}

async function renderPdfPage(pdf: PDFDocumentProxy, pageNumber: number, rotation: number, maxEdge: number): Promise<{ dataUrl: string; width: number; height: number }> {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1, rotation });
  const scale = Math.min(1, maxEdge / Math.max(viewport.width, viewport.height), Math.sqrt(MAX_PDF_RENDER_PIXELS / (viewport.width * viewport.height)));
  const width = Math.max(1, Math.round(viewport.width * scale));
  const height = Math.max(1, Math.round(viewport.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const canvasContext = canvas.getContext('2d');
  if (!canvasContext) throw new Error('Die PDF-Vorschau konnte nicht vorbereitet werden.');
  await page.render({ canvasContext, viewport: page.getViewport({ scale, rotation }) }).promise;
  page.cleanup();
  return { dataUrl: canvas.toDataURL('image/png'), width, height };
}

function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.click();
}

function cropDataUrl(dataUrl: string, crop: FloorPlanCropRegion | null): Promise<string> {
  if (!crop) return Promise.resolve(dataUrl);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * crop.width));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * crop.height));
      const context = canvas.getContext('2d');
      if (!context) { reject(new Error('Der PDF-Ausschnitt konnte nicht vorbereitet werden.')); return; }
      context.drawImage(image, Math.round(image.naturalWidth * crop.x), Math.round(image.naturalHeight * crop.y), canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => reject(new Error('Die PDF-Vorschau konnte nicht gelesen werden.'));
    image.src = dataUrl;
  });
}

export default function FloorPlanArea({ view, active, apiReady, objectId, restoredSource, restoredVersion, onVersionsChanged, onOrientationSaving, onOpen2d, onOpen3d }: { onVersionsChanged?: () => void; onOrientationSaving?: (saving: boolean) => void; restoredVersion?: { id: string; requestId: string } | null; restoredSource?: { file: File; fileId: string } | null; view: FloorPlanView; active: boolean; apiReady: boolean; objectId?: string | null; onOpen2d: () => void; onOpen3d: () => void }) {
  const [model, setModel] = useState(createFloorPlanProject);
  const [modelView, setModelView] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [sourceFileId, setSourceFileId] = useState<string | null>(null);
  const [originalPreview, setOriginalPreview] = useState('');
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfRotation, setPdfRotation] = useState(0);
  const [pdfPreview, setPdfPreview] = useState('');
  const [pdfThumbnails, setPdfThumbnails] = useState<Array<{ page: number; src: string }>>([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cleanResult, setCleanResult] = useState<FloorPlanResult | null>(null);
  const [threeDResult, setThreeDResult] = useState<FloorPlanResult | null>(null);
  const [cropRegion, setCropRegion] = useState<FloorPlanCropRegion | null>(null);
  const [cropSelecting, setCropSelecting] = useState(false);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [cropDraft, setCropDraft] = useState<FloorPlanCropRegion | null>(null);
  const [floorTreatment, setFloorTreatment] = useState<FloorPlanSurfaceMode>('pastel');
  const [editRegion, setEditRegion] = useState<FloorPlanCropRegion | null>(null);
  const [localEdit, setLocalEdit] = useState(false);
  const editStart = useRef<{x: number; y: number} | null>(null);
  const editTransform = useRef<{mode: string; region: FloorPlanCropRegion} | null>(null);
  const [previousResult, setPreviousResult] = useState<FloorPlanResult | null>(null);
  const [correction, setCorrection] = useState('');
  const [labels, setLabels] = useState<FloorPlanLabel[]>([]);
  const [labelStyle, setLabelStyle] = useState<FloorPlanLabelStyle>(() => ({ ...DEFAULT_FLOORPLAN_LABEL_STYLE }));
  const [addingLabel, setAddingLabel] = useState(false);
  const [newLabelPosition, setNewLabelPosition] = useState<{ x: number; y: number } | null>(null);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelArea, setNewLabelArea] = useState('');
  const [draggingLabel, setDraggingLabel] = useState<string | null>(null);
  const starting2d = useRef(false);
  const startFeedback = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<'2d' | '3d' | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [previewMode, setPreviewMode] = useState<FloorPlanPreviewMode>('original');
  const [orientationError, setOrientationError] = useState('');
  const activeObjectId = useRef(objectId); activeObjectId.current = objectId;
  const [orientationSaving, setOrientationSaving] = useState(false);
  const orientationBusy = useRef(false);
  const orientationOperation = useRef<{ versionId: string; operationId: string } | null>(null);
  const [previewLoadedSource, setPreviewLoadedSource] = useState('');
  const fittedSourceRef = useRef('');
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState<FloorPlanPreviewPan>({ x: 0, y: 0 });
  const [draggingPreview, setDraggingPreview] = useState(false);
  const labelStageRef = useRef<HTMLDivElement>(null);
  const labelDocumentInputRef = useRef<HTMLInputElement>(null);
  const [labelStageSize, setLabelStageSize] = useState({ width: 0, height: 0 });
  const [labelImageDimensions, setLabelImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const previewDragRef = useRef<PreviewDragState | null>(null);
  const previewDragMovedRef = useRef(false);

  useEffect(() => () => {
    if (originalPreview.startsWith('blob:')) URL.revokeObjectURL(originalPreview);
  }, [originalPreview]);

  const workingImage = cleanResult?.image || originalPreview;
  const previewImage = previewMode === 'original'
    ? (cleanResult?.originalImage || originalPreview)
    : cleanResult?.image || originalPreview;
  const canShowComparison = Boolean(cleanResult?.image && cleanResult.originalImage);
  const canCreate3d = Boolean(file && workingImage && apiReady && !loading);
  const labelsDescription = labels.length === 0
    ? 'Noch keine Beschriftungen. Klicke auf das Bild, um einen Raumnamen hinzuzufügen.'
    : `${labels.length} Beschriftung${labels.length === 1 ? '' : 'en'} als eigene Ebene`;

  const previewImageRect: FloorPlanPreviewRect = labelImageDimensions
    ? getContainedImageRect(labelStageSize.width, labelStageSize.height, labelImageDimensions.width, labelImageDimensions.height)
    : { left: 0, top: 0, width: labelStageSize.width, height: labelStageSize.height };

  const labelPreviewFontSize = labelImageDimensions && previewImageRect.width
    ? Math.round(getFloorPlanLabelFontSizes(labelImageDimensions.width, labelImageDimensions.height, labelStyle).name * (previewImageRect.width / labelImageDimensions.width))
    : undefined;

  useEffect(() => {
    const stage = labelStageRef.current;
    if (!stage) return undefined;
    const updateSize = () => setLabelStageSize({ width: stage.clientWidth, height: stage.clientHeight });
    updateSize();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateSize);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [workingImage, previewMode, active, view]);

  useEffect(() => {
    if (!active || view !== '2d' || !previewImage || previewLoadedSource !== previewImage
      || !labelImageDimensions || labelStageSize.width <= 0 || labelStageSize.height <= 0
      || fittedSourceRef.current === previewImage) return;
    resetPreviewView();
    setDraggingPreview(false);
    previewDragRef.current = null;
    const frame = requestAnimationFrame(() => {
      fittedSourceRef.current = previewImage;
      labelStageRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [previewImage, previewLoadedSource, labelImageDimensions, labelStageSize, active, view]);

  useEffect(() => {
    setPreviewPan(current => clampPreviewPan(current, labelStageSize.width, labelStageSize.height, previewImageRect, previewZoom));
  }, [labelStageSize.width, labelStageSize.height, previewImageRect.width, previewImageRect.height]);

  useEffect(() => {
    setPreviewMode(cleanResult ? 'result' : 'original');
  }, [cleanResult]);

  useEffect(() => () => { if (pdfDocument) void pdfDocument.destroy(); }, [pdfDocument]);

  useEffect(() => {
    if (!pdfDocument) return;
    let cancelled = false;
    void renderPdfPage(pdfDocument, pdfPage, pdfRotation, MAX_PDF_RENDER_EDGE).then((rendered) => { if (!cancelled) setPdfPreview(rendered.dataUrl); }).catch(() => { if (!cancelled) setError('Die gewählte PDF-Seite konnte nicht gerendert werden.'); });
    return () => { cancelled = true; };
  }, [pdfDocument, pdfPage, pdfRotation]);

  useEffect(() => {
    if (!objectId) return;
    let cancelled = false;
    void authenticatedFetch(`/api/objects/${objectId}`).then(async (response) => {
      if (!response.ok) return null;
      return response.json() as Promise<{ versions?: Array<{id: string; mode: string; status: string}>; floorplanSources?: Array<{ fileId: string; filename: string }> }>;
    }).then(async (payload) => {
      const latest = payload?.versions?.find(v => v.mode === 'floorplan-2d' && v.status === 'succeeded');
      if (latest && !cancelled) { await openResultVersion(latest.id); return; }
      const source = payload?.floorplanSources?.at(-1); if (!source || cancelled) return;
      const restored = await fetchPrivateFile(source.fileId, source.filename || 'grundriss-pdf-seite.png'); if (cancelled) return;
      if (originalPreview.startsWith('blob:')) URL.revokeObjectURL(originalPreview);
      setFile(restored); setSourceFileId(source.fileId); setOriginalPreview(URL.createObjectURL(restored)); setMessage('Gespeicherte PDF-Grundriss-Eingabe geladen.');
    }).catch(() => { /* A missing historical source must not disturb the current editor. */ });
    return () => { cancelled = true; };
  }, [objectId]);

  function cancelPdfImport() {
    if (pdfDocument) void pdfDocument.destroy();
    setPdfDocument(null); setPdfFile(null); setPdfPageCount(0); setPdfPage(1); setPdfRotation(0); setPdfPreview(''); setPdfThumbnails([]); setPdfBusy(false); setError('');
  }

  async function loadPdfFile(nextFile: File) {
    if (!objectId) { setError('Wähle vor dem PDF-Import eine Objektmappe aus.'); return; }
    if (nextFile.size > MAX_PDF_BYTES) { setError('Das PDF ist zu gross. Bitte verwende maximal 20 MB.'); return; }
    setPdfBusy(true); setError('');
    let nextDocument: PDFDocumentProxy | null = null;
    try {
      const bytes = new Uint8Array(await nextFile.arrayBuffer());
      const header = new TextDecoder('ascii').decode(bytes.slice(0, 8)); const tail = new TextDecoder('latin1').decode(bytes.slice(Math.max(0, bytes.length - 1024 * 1024)));
      if (!header.startsWith('%PDF-') || !tail.includes('%%EOF')) throw new Error('Die Datei ist kein gültiges oder vollständiges PDF.');
      const sourceText = new TextDecoder('latin1').decode(bytes);
      if (/\/Encrypt\b|\/JavaScript\b|\/EmbeddedFiles\b|\/EmbeddedFile\b|\/Launch\b/i.test(sourceText)) throw new Error('Dieses PDF enthält verschlüsselte Inhalte, Skripte oder Anhänge und wird aus Sicherheitsgründen abgelehnt.');
      nextDocument = await getDocument({ data: bytes, disableAutoFetch: true, disableStream: true, isEvalSupported: false, enableXfa: false, useWorkerFetch: false }).promise;
      if (nextDocument.numPages < 1 || nextDocument.numPages > MAX_PDF_PAGES) throw new Error(`Das PDF enthält zu viele Seiten. Erlaubt sind maximal ${MAX_PDF_PAGES}.`);
      const thumbnails: Array<{ page: number; src: string }> = [];
      for (let page = 1; page <= nextDocument.numPages; page += 1) thumbnails.push({ page, src: (await renderPdfPage(nextDocument, page, 0, 180)).dataUrl });
      if (pdfDocument) void pdfDocument.destroy();
      setPdfDocument(nextDocument); setPdfFile(nextFile); setPdfPageCount(nextDocument.numPages); setPdfPage(1); setPdfRotation(0); setPdfThumbnails(thumbnails); setPdfPreview((await renderPdfPage(nextDocument, 1, 0, MAX_PDF_RENDER_EDGE)).dataUrl); setCropRegion(null); setCropDraft(null); setCropSelecting(false); setMessage('PDF-Seite vorbereitet. Wähle Seite, Drehung und optional einen Ausschnitt. Es wurde keine KI-Bearbeitung gestartet.');
    } catch (pdfError) { if (nextDocument) void nextDocument.destroy(); setError(pdfError instanceof Error ? pdfError.message : 'Das PDF konnte nicht verarbeitet werden.'); }
    finally { setPdfBusy(false); }
  }

  async function acceptPdfImport() {
    if (!pdfFile || !pdfPreview || !objectId || !pdfDocument || pdfBusy) return;
    setPdfBusy(true); setError(''); setMessage('PDF-Seite wird als neue Grundriss-Eingabe gespeichert …');
    try {
      const rendered = await renderPdfPage(pdfDocument, pdfPage, pdfRotation, MAX_PDF_RENDER_EDGE); const cropped = await cropDataUrl(rendered.dataUrl, cropRegion); const image = dataUrlToFile(cropped, `grundriss-pdf-seite-${pdfPage}.png`);
      const form = new FormData(); form.append('objectId', objectId); form.append('pdf', pdfFile, pdfFile.name); form.append('image', image, image.name); form.append('page', String(pdfPage)); form.append('pageCount', String(pdfPageCount)); form.append('rotation', String(pdfRotation)); form.append('renderWidth', String(rendered.width)); form.append('renderHeight', String(rendered.height)); if (cropRegion) form.append('crop', JSON.stringify(cropRegion));
      const response = await authenticatedFetch('/api/floorplan/import', { method: 'POST', body: form }); const payload = await response.json() as ApiErrorPayload & { sourceFileId?: string };
      if (!response.ok || !payload.sourceFileId) throw new Error(payload.error?.message || 'Der PDF-Grundriss konnte nicht übernommen werden.');
      const nextFile = dataUrlToFile(cropped, `grundriss-pdf-seite-${pdfPage}.png`); if (originalPreview.startsWith('blob:')) URL.revokeObjectURL(originalPreview);
      setFile(nextFile); setSourceFileId(payload.sourceFileId); setOriginalPreview(URL.createObjectURL(nextFile)); setCleanResult(null); setThreeDResult(null); setPreviousResult(null); setEditRegion(null); setLabels([]); setLabelImageDimensions(null); setPreviewMode('original'); setCropRegion(null); setCropDraft(null); setCropSelecting(false); cancelPdfImport(); setMessage('Grundriss übernommen. Prüfe die Eingabe und starte eine KI-Bearbeitung erst ausdrücklich.');
    } catch (importError) { setError(importError instanceof Error ? importError.message : 'Der PDF-Grundriss konnte nicht übernommen werden.'); setMessage(''); }
    finally { setPdfBusy(false); }
  }

  function rejectFile(reason: string) {
    setFile(null);
    setSourceFileId(null);
    if (originalPreview.startsWith('blob:')) URL.revokeObjectURL(originalPreview);
    setOriginalPreview('');
    setCleanResult(null);
    setThreeDResult(null);
    setPreviewMode('original');
    setCropRegion(null);
    setCropSelecting(false);
    setCropStart(null);
    setCropDraft(null);
    setLabels([]);
    setLabelImageDimensions(null);
    setError(reason);
    setMessage('');
  }

  function handleFile(nextFile?: File) {
    if (!nextFile) return;
    if (nextFile.type === 'application/pdf' || nextFile.name.toLowerCase().endsWith('.pdf')) { void loadPdfFile(nextFile); return; }
    if (!ACCEPTED_TYPES.includes(nextFile.type)) { setError('Bitte wähle ein JPG-, PNG- oder WebP-Grundrissbild.'); return; }
    if (nextFile.size > MAX_UPLOAD_BYTES) { setError('Das Grundrissbild ist zu gross. Bitte verwende maximal 15 MB.'); return; }
    if (!ACCEPTED_TYPES.includes(nextFile.type)) {
      rejectFile('Bitte wähle ein JPG-, PNG- oder WebP-Grundrissbild.');
      return;
    }
    if (nextFile.size > MAX_UPLOAD_BYTES) {
      rejectFile('Das Grundrissbild ist zu gross. Bitte verwende maximal 15 MB.');
      return;
    }
    if (originalPreview.startsWith('blob:')) URL.revokeObjectURL(originalPreview);
    setFile(nextFile);
    setSourceFileId(null);
    setOriginalPreview(URL.createObjectURL(nextFile));
    setEditRegion(null); setPreviousResult(null); setLocalEdit(false);
    setCleanResult(null);
    setThreeDResult(null);
    setPreviewMode('original');
    setCropRegion(null);
    setCropSelecting(false);
    setCropStart(null);
    setCropDraft(null);
    setLabels([]);
    setLabelImageDimensions(null);
    setCorrection('');
    setError('');
    setMessage('Grundriss bereit. Klicke auf «Sauberen 2D-Grundriss erzeugen», um die KI-Visualisierung bewusst zu starten.');
  }

  useEffect(() => {
    if (!restoredSource) return;
    cancelPdfImport();
    handleFile(restoredSource.file);
    setSourceFileId(restoredSource.fileId);
    setMessage('Gespeichertes Grundrissoriginal geladen. Starte die 2D-Erstellung ausdrücklich per Klick.');
  }, [restoredSource]);

  async function openResultVersion(versionId: string) {
    if (!objectId || orientationBusy.current) return;
    try {
      const response = await authenticatedFetch(`/api/objects/${objectId}/versions/${versionId}/orientation`);
      const result = await response.json() as FloorPlanResult & ApiErrorPayload;
      if (!response.ok) throw new Error(result.error?.message || 'Das 2D-Ergebnis konnte nicht geöffnet werden.');
      if (activeObjectId.current !== objectId) return;
      if (result.sourceFileId) {
        const originalFile = await fetchPrivateFile(result.sourceFileId, result.sourceFilename || 'grundriss-original.png');
        setFile(originalFile); setSourceFileId(result.sourceFileId); setOriginalPreview(URL.createObjectURL(originalFile));
      }
      setCleanResult(result); setPreviewMode('result'); setEditRegion(null); setLocalEdit(false); setPreviousResult(null);
      setLabels([]); setError(''); setMessage(`Gespeichertes 2D-Ergebnis geöffnet · Ausrichtung ${result.degrees || 0}°. Alte Bearbeitungsbereiche wurden zurückgesetzt.`);
    } catch (error) { setError(error instanceof Error ? error.message : 'Das Ergebnis konnte nicht geöffnet werden.'); }
  }
  useEffect(() => { if (restoredVersion) void openResultVersion(restoredVersion.id); }, [restoredVersion]);

  async function rotateResult() {
    if (orientationBusy.current || loading || !cleanResult?.versionId || !objectId) return;
    const versionId = cleanResult.versionId;
    const operation = orientationOperation.current?.versionId === versionId ? orientationOperation.current : { versionId, operationId: crypto.randomUUID() };
    orientationOperation.current = operation;
    orientationBusy.current = true; setOrientationError(''); setOrientationSaving(true); onOrientationSaving?.(true); setError(''); setMessage('Ausrichtung wird gespeichert …');
    try {
      const response = await authenticatedFetch(`/api/objects/${objectId}/versions/${versionId}/orientation`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ degrees: 90, operationId: operation.operationId }) });
      const result = await response.json() as FloorPlanResult & ApiErrorPayload;
      if (!response.ok) throw new Error(result.error?.message || 'Die Ausrichtung wurde nicht gespeichert.');
      const delta = ((result.degrees || 0) - (cleanResult.degrees || 0) + 360) % 360;
      setLabels(current => current.map(label => {
        let {x,y} = label; for (let i = 0; i < delta; i += 90) [x,y] = [1-y,x];
        return { ...label, x, y, rotation: normalizeFloorPlanRotation((label.rotation || 0) + delta) };
      }));
      setCleanResult({ ...cleanResult, ...result }); setPreviewMode('result'); setEditRegion(null); setLocalEdit(false); setNewLabelPosition(null); setAddingLabel(false);
      orientationOperation.current = null;
      onVersionsChanged?.();
      setMessage(`Ausrichtung ${result.degrees || 0}° gespeichert. Alte Bearbeitungsbereiche wurden zurückgesetzt.`);
    } catch (error) { setOrientationError(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.'); setMessage(''); setError(`${error instanceof Error ? error.message : 'Speichern fehlgeschlagen.'} Wiederhole den Speicherversuch bewusst; derselbe Drehvorgang wird nicht doppelt angewendet.`); }
    finally { orientationBusy.current = false; setOrientationSaving(false); onOrientationSaving?.(false); }
  }

  function startCropSelection(event: React.PointerEvent<HTMLDivElement>) {
    if (!cropSelecting) return;
    event.preventDefault();
    event.stopPropagation();
    const point = getStagePoint(event as unknown as React.MouseEvent<HTMLDivElement>, event.currentTarget);
    setCropStart(point);
    setCropDraft({ ...point, width: 0, height: 0 });
  }

  function moveCropSelection(event: React.PointerEvent<HTMLDivElement>) {
    if (!cropSelecting || !cropStart) return;
    event.preventDefault();
    event.stopPropagation();
    const point = getStagePoint(event as unknown as React.MouseEvent<HTMLDivElement>, event.currentTarget);
    setCropDraft({
      x: Math.min(cropStart.x, point.x),
      y: Math.min(cropStart.y, point.y),
      width: Math.abs(point.x - cropStart.x),
      height: Math.abs(point.y - cropStart.y),
    });
  }

  function finishCropSelection(event: React.PointerEvent<HTMLDivElement>) {
    if (!cropSelecting || !cropStart) return;
    event.preventDefault();
    event.stopPropagation();
    const point = getStagePoint(event as unknown as React.MouseEvent<HTMLDivElement>, event.currentTarget);
    const nextRegion: FloorPlanCropRegion = {
      x: Math.min(cropStart.x, point.x),
      y: Math.min(cropStart.y, point.y),
      width: Math.abs(point.x - cropStart.x),
      height: Math.abs(point.y - cropStart.y),
    };
    if (nextRegion.width < 0.02 || nextRegion.height < 0.02) {
      setCropRegion(null);
      setCropDraft(null);
      setError('Bitte markiere einen grösseren rechteckigen Ausschnitt, der den vollständigen Grundriss enthält.');
    } else {
      setCropRegion(nextRegion);
      setCropDraft(nextRegion);
      setError('');
      setMessage('Ausschnitt gespeichert. Prüfe, dass der vollständige gewünschte Grundriss innerhalb des Rahmens liegt.');
    }
    setCropStart(null);
    setCropSelecting(false);
  }

  async function create2d() {
    if (starting2d.current || loading || orientationBusy.current) return;
    startFeedback.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
    if (!objectId) { setError('Wähle zuerst eine Objektmappe unter «Meine Objekte» aus.'); return; }
    if (pdfFile) { setError('Bitte übernimm zuerst die ausgewählte PDF-Seite mit «Grundriss übernehmen».'); return; }
    if (!file) { setError('Bitte lade zuerst einen Grundriss hoch.'); return; }
    if (!apiReady) { setError('API-Schlüssel fehlt. Es wird kein simuliertes Ergebnis erzeugt.'); return; }
    const normalizedCorrection = correction.trim();
    if (normalizedCorrection.length > MAX_FLOORPLAN_CORRECTION_LENGTH) {
      setError(`Die Korrekturanweisung ist zu lang. Bitte beschränke sie auf maximal ${MAX_FLOORPLAN_CORRECTION_LENGTH} Zeichen.`);
      return;
    }
    if (localEdit && (!editRegion || !normalizedCorrection || !cleanResult)) { setError('Bitte Bereich und Korrekturtext angeben.'); return; }
    starting2d.current = true;
    setLoading('2d'); setError(''); setMessage(normalizedCorrection ? 'Der aktuelle Entwurf wird anhand des Originals korrigiert …' : 'Der Grundriss wird als saubere 2D-KI-Visualisierung aufbereitet …');
    try {
      const form = new FormData();
      if (sourceFileId) form.append('sourceFileId', sourceFileId); else form.append('image', file, file.name);
      if (objectId) form.append('objectId', objectId);
      form.append('correction', normalizedCorrection);
      form.append('floorTreatment', 'pastel');
      if (localEdit && editRegion) form.append('editRegion', JSON.stringify(editRegion));
      if (cropRegion) form.append('cropRegion', JSON.stringify(cropRegion));
      if (normalizedCorrection && cleanResult) {
        if (cleanResult.versionId) { form.append('draftVersionId', cleanResult.versionId); form.append('draftOrientationRevision', String(cleanResult.revision || 0)); }
        const draft = cleanResult.resultFileId
          ? await fetchPrivateFile(cleanResult.resultFileId, 'aktueller-2d-entwurf.png')
          : dataUrlToFile(cleanResult.image, 'aktueller-2d-entwurf.png');
        form.append('draftImage', draft);
      }
      const response = await authenticatedFetch('/api/floorplan/2d', { method: 'POST', body: form });
      const payload = await response.json().catch(() => { throw new Error('Der Server hat keine lesbare Antwort geliefert. Bitte prüfe die Verbindung und starte bei Bedarf bewusst erneut.'); }) as FloorPlanResult & ApiErrorPayload;
      if (!response.ok) throw new Error(payload.error?.message || 'Der 2D-Grundriss konnte nicht erstellt werden.');
      if (!payload.imageUrl && !payload.image) throw new Error('Der Bildanbieter hat kein verwendbares Ergebnis geliefert. Du kannst die Erstellung bewusst erneut starten.');
      setPreviousResult(cleanResult);
      setEditRegion(null);
      setCleanResult({
        ...payload,
        image: payload.imageUrl || payload.image || '',
        originalImage: payload.originalImageUrl || payload.originalImage || originalPreview,
      });
      setFloorTreatment(payload.floorTreatment || floorTreatment);
      setThreeDResult(null);
      setPreviewMode('result');
      onVersionsChanged?.();
      setMessage('2D-Entwurf bereit. Vergleiche ihn mit dem Original und korrigiere fehlende Türen, Fenster oder Treppen gezielt.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Der 2D-Grundriss konnte nicht erstellt werden.');
      setMessage('');
    } finally { starting2d.current = false; setLoading(null); }
  }

  async function create3d() {
    if (!objectId) { setError('Wähle zuerst eine Objektmappe unter «Meine Objekte» aus.'); return; }
    if (!file || !workingImage) { setError('Lade zuerst einen Grundriss hoch oder erstelle einen 2D-Entwurf.'); return; }
    if (!apiReady) { setError('API-Schlüssel fehlt. Es wird kein simuliertes Ergebnis erzeugt.'); return; }
    setLoading('3d'); setError(''); setMessage('Die statische 3D-KI-Visualisierung wird bewusst erstellt …');
    try {
      const form = new FormData();
      const source = cleanResult?.resultFileId
        ? await fetchPrivateFile(cleanResult.resultFileId, 'sauberer-2d-grundriss.png')
        : file;
      if (sourceFileId && !cleanResult) form.append('sourceFileId', sourceFileId); else form.append('image', source);
      if (objectId) form.append('objectId', objectId);
      form.append('floorTreatment', floorTreatment);
      if (cropRegion && !cleanResult) form.append('cropRegion', JSON.stringify(cropRegion));
      const response = await authenticatedFetch('/api/floorplan/3d', { method: 'POST', body: form });
      const payload = await response.json().catch(() => { throw new Error('Der Server hat keine lesbare Antwort geliefert. Bitte prüfe die Verbindung und starte bei Bedarf bewusst erneut.'); }) as FloorPlanResult & ApiErrorPayload;
      if (!response.ok) throw new Error(payload.error?.message || 'Die 3D-Visualisierung konnte nicht erstellt werden.');
      setThreeDResult({
        ...payload,
        image: payload.imageUrl || payload.image || '',
        originalImage: payload.originalImageUrl || payload.originalImage || originalPreview,
      });
      setMessage('Statische 3D-Visualisierung bereit. Sie ist weder interaktiv noch vermessen.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Die 3D-Visualisierung konnte nicht erstellt werden.');
      setMessage('');
    } finally { setLoading(null); }
  }

  function handleStageClick(event: React.MouseEvent<HTMLDivElement>) {
    if (localEdit || !addingLabel || previewDragMovedRef.current || event.target !== event.currentTarget && (event.target as HTMLElement).tagName !== 'IMG') return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const point = mapPreviewPointToImage(event.clientX - bounds.left, event.clientY - bounds.top, previewImageRect, previewZoom, previewPan);
    setNewLabelPosition(point);
    setNewLabelName('');
    setNewLabelArea('');
  }

  function startPreviewPan(event: React.PointerEvent<HTMLDivElement>) {
    if (localEdit && previewMode === 'result') {
      event.preventDefault(); const bounds = event.currentTarget.getBoundingClientRect();
      editStart.current = mapPreviewPointToImage(event.clientX - bounds.left, event.clientY - bounds.top, previewImageRect, previewZoom, previewPan);
      const mode = (event.target as HTMLElement).dataset.regionAction;
      editTransform.current = mode && editRegion ? {mode, region: editRegion} : null;
      event.currentTarget.setPointerCapture(event.pointerId); return;
    }
    if (previewZoom <= 1 || addingLabel) return;
    event.preventDefault();
    previewDragMovedRef.current = false;
    previewDragRef.current = { startX: event.clientX, startY: event.clientY, startPan: previewPan, pointerId: event.pointerId };
    setDraggingPreview(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function setPreviewZoomValue(nextZoom: number) {
    const zoom = Math.max(1, Math.min(3, nextZoom));
    setPreviewZoom(zoom);
    setPreviewPan((current) => clampPreviewPan(current, labelStageSize.width, labelStageSize.height, previewImageRect, zoom));
  }

  function resetPreviewView() {
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
    previewDragMovedRef.current = false;
    if (labelStageRef.current) { labelStageRef.current.scrollTop = 0; labelStageRef.current.scrollLeft = 0; }
  }

  function saveNewLabel() {
    const name = newLabelName.trim();
    if (!name || !newLabelPosition) { setError('Bitte gib einen Raumnamen ein und klicke zuerst auf das Bild.'); return; }
    setLabels((current) => [...current, { id: `label-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...newLabelPosition, name, area: normalizeFloorPlanArea(newLabelArea), rotation: 0 }]);
    setNewLabelPosition(null); setNewLabelName(''); setNewLabelArea(''); setAddingLabel(false); setError('');
  }

  function updateLabel(id: string, patch: Partial<FloorPlanLabel>) {
    setLabels((current) => current.map((label) => label.id === id ? { ...label, ...patch } : label));
  }

  function applyDefaultLabelStyle() {
    setLabelStyle({ ...DEFAULT_FLOORPLAN_LABEL_STYLE });
    setMessage('Die Standardgestaltung wurde auf die Beschriftungsebene angewendet.');
    setError('');
  }

  function saveLabelDocument() {
    try {
      const documentData = createFloorPlanLabelDocument(labels, labelStyle);
      const blob = new Blob([JSON.stringify(documentData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'property-partners-grundriss-beschriftungen.json';
      link.click();
      URL.revokeObjectURL(url);
      setMessage('Beschriftungen und Gestaltungseinstellungen wurden lokal gespeichert.');
      setError('');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Die Beschriftungen konnten nicht gespeichert werden.');
    }
  }

  async function loadLabelDocument(nextFile?: File) {
    if (!nextFile) return;
    try {
      const parsed = normalizeFloorPlanLabelDocument(JSON.parse(await nextFile.text()));
      setLabels(parsed.labels);
      setLabelStyle(parsed.style);
      setNewLabelPosition(null);
      setAddingLabel(false);
      setMessage('Beschriftungen und Gestaltungseinstellungen wurden geladen.');
      setError('');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Die Beschriftungsdatei konnte nicht geladen werden.');
    } finally {
      if (labelDocumentInputRef.current) labelDocumentInputRef.current.value = '';
    }
  }

  async function saveStudioProject() {
    if (!file || !cleanResult) return;
    const source = await fileToDataUrl(file);
    const resultImage = cleanResult.resultFileId
      ? await fetchPrivateFileDataUrl(cleanResult.resultFileId, 'grundriss-2d.png')
      : cleanResult.image;
    const savedResult = {
      ...cleanResult,
      image: resultImage,
      originalImage: source,
      imageUrl: undefined,
      originalImageUrl: undefined,
      downloadUrl: undefined,
      originalDownloadUrl: undefined,
      correctedImage: undefined,
      distortion: undefined,
    };
    const blob = new Blob([JSON.stringify({ version: 1, source, result: savedResult, labels: createFloorPlanLabelDocument(labels, labelStyle) })], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    downloadDataUrl(url, 'grundriss-studio.json');
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  async function loadStudioProject(input?: File) {
    if (!input) return;
    try {
      if (input.size > 60*1024*1024) throw new Error('Projekt zu gross.');
      const saved = JSON.parse(await input.text());
      const image = (value: unknown): value is string => typeof value === 'string' && /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(value);
      if (saved.version !== 1 || !image(saved.source) || !image(saved.result?.image) || !image(saved.result?.originalImage)) throw new Error('Ungültiges Grundrissprojekt.');
      const document = normalizeFloorPlanLabelDocument(saved.labels);
      let restoredResult = { ...saved.result, versionId: undefined, resultFileId: undefined };
      if (saved.result.versionId && objectId) {
        const response = await authenticatedFetch(`/api/objects/${objectId}/versions/${saved.result.versionId}/orientation`);
        if (!response.ok) throw new Error('Die gespeicherte Ergebnisversion ist in dieser Objektmappe nicht verfügbar.');
        const current = await response.json() as FloorPlanResult;
        const delta = ((current.degrees || 0) - (saved.result.degrees || 0) + 360) % 360;
        document.labels = document.labels.map(label => { let { x, y } = label; for (let i = 0; i < delta; i += 90) [x, y] = [1-y, x]; return { ...label, x, y, rotation: normalizeFloorPlanRotation((label.rotation || 0) + delta) }; });
        restoredResult = { ...saved.result, ...current };
      }
      setFile(dataUrlToFile(saved.source,'grundriss.png')); setOriginalPreview(saved.source); setCleanResult({...restoredResult, correctedImage:undefined,distortion:undefined});setLabels(document.labels);setLabelStyle(document.style);setEditRegion(null);setPreviousResult(null);setLocalEdit(false);setThreeDResult(null);setError('');
    } catch { setError('Das Grundrissprojekt konnte nicht sicher geladen werden.'); }
  }

  function handleLabelPointerDown(event: React.PointerEvent<HTMLButtonElement>, id: string) {
    event.stopPropagation();
    setDraggingLabel(id);
  }

  function handleStagePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (editStart.current) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const point = mapPreviewPointToImage(event.clientX-bounds.left,event.clientY-bounds.top,previewImageRect,previewZoom,previewPan);
      const start=editStart.current, transform=editTransform.current;
      if (transform) {
        const r=transform.region, dx=point.x-start.x,dy=point.y-start.y;
        if (transform.mode==='move') setEditRegion({...r,x:Math.max(0,Math.min(1-r.width,r.x+dx)),y:Math.max(0,Math.min(1-r.height,r.y+dy))});
        else setEditRegion({...r,width:Math.max(.001,Math.min(1-r.x,r.width+dx)),height:Math.max(.001,Math.min(1-r.y,r.height+dy))});
      } else setEditRegion({x:Math.min(start.x,point.x),y:Math.min(start.y,point.y),width:Math.abs(start.x-point.x),height:Math.abs(start.y-point.y)});
      return;
    }
    if (draggingLabel) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const point = mapPreviewPointToImage(event.clientX - bounds.left, event.clientY - bounds.top, previewImageRect, previewZoom, previewPan);
      updateLabel(draggingLabel, point);
      return;
    }
    const drag = previewDragRef.current;
    if (!drag) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) previewDragMovedRef.current = true;
    setPreviewPan(clampPreviewPan({ x: drag.startPan.x + deltaX, y: drag.startPan.y + deltaY }, labelStageSize.width, labelStageSize.height, previewImageRect, previewZoom));
  }

  function stopDragging(event?: React.PointerEvent<HTMLDivElement>) {
    editStart.current = null;
    setDraggingLabel(null);
    if (event && event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    previewDragRef.current = null;
    setDraggingPreview(false);
  }

  function selectPreviewMode(nextMode: FloorPlanPreviewMode) {
    if (nextMode === 'result' && !cleanResult) return;
    if (nextMode === 'compare' && !canShowComparison) return;
    setPreviewMode(nextMode);
    setAddingLabel(false);
    if (nextMode !== 'result') setNewLabelPosition(null);
    resetPreviewView();
  }

  async function downloadFloorPlanResult(result: FloorPlanResult | null, fallback: string, filename: string) {
    filename = result?.downloadFilename || filename;
    try {
      if (!result?.resultFileId) {
        downloadDataUrl(fallback, filename);
        return;
      }
      const file = await fetchPrivateFile(result.resultFileId, filename);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'Die Grundriss-Rohdatei konnte nicht heruntergeladen werden.');
    }
  }

  async function downloadLabels() {
    if (!workingImage) { setError('Erstelle zuerst einen 2D-Entwurf oder lade ein Grundrissbild hoch.'); return; }
    const versionSuffix = cleanResult?.processingVersion ? `-${cleanResult.processingVersion}` : '';
    try {
      const imageSource = cleanResult?.resultFileId
        ? await fetchPrivateFileDataUrl(cleanResult.resultFileId, `property-partners-grundriss-2d${versionSuffix}.png`)
        : workingImage;
      if (labels.length === 0) {
        downloadDataUrl(imageSource, `property-partners-grundriss-2d${versionSuffix}.png`);
        return;
      }
      const image = new Image();
      image.src = imageSource;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Der PNG-Export ist in diesem Browser nicht verfügbar.');
      context.drawImage(image, 0, 0);
      const labelFontSizes = getFloorPlanLabelFontSizes(canvas.width, canvas.height, labelStyle);
      const nameFontSize = labelFontSizes.name;
      const areaFontSize = labelFontSizes.area;
      await document.fonts?.load(`${labelStyle.nameWeight} ${nameFontSize}px "DM Sans"`);
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      for (const label of labels) {
        const x = label.x * canvas.width;
        const y = label.y * canvas.height;
        const area = normalizeFloorPlanArea(label.area);
        const rotation = normalizeFloorPlanRotation(label.rotation);
        const lineHeight = nameFontSize * labelStyle.lineHeight;
        context.font = `${labelStyle.nameWeight} ${nameFontSize}px "DM Sans", Arial, sans-serif`;
        const nameWidth = context.measureText(label.name).width;
        context.font = `400 ${areaFontSize}px "DM Sans", Arial, sans-serif`;
        const areaWidth = area ? context.measureText(area).width : 0;
        const maxWidth = Math.max(nameWidth, areaWidth);
        const totalHeight = area ? lineHeight + areaFontSize * labelStyle.lineHeight : nameFontSize;
        context.save();
        context.translate(x, y);
        context.rotate(rotation * Math.PI / 180);

        context.font = `${labelStyle.nameWeight} ${nameFontSize}px "DM Sans", Arial, sans-serif`;
        context.fillStyle = labelStyle.nameColour;
        context.fillText(label.name, 0, area ? -(lineHeight * 0.52) : 0);
        if (area) {
          context.font = `400 ${areaFontSize}px "DM Sans", Arial, sans-serif`;
          context.fillStyle = labelStyle.areaColour;
          context.fillText(area, 0, lineHeight * 0.52);
        }
        context.restore();
      }
      downloadDataUrl(canvas.toDataURL('image/png'), `property-partners-grundriss-2d-beschriftet${versionSuffix}.png`);
    } catch (exportError) { setError(exportError instanceof Error ? exportError.message : 'Der beschriftete PNG-Export ist fehlgeschlagen.'); }
  }

  const sourceFileName = file?.name || 'Noch kein Grundriss ausgewählt';

  if (view === '3d') {
    return <div hidden={!active} className="floorplan-area wrap" aria-label="3D-Grundriss Visualisierung">
      <section className="area-heading"><p className="eyebrow">Roadmap-Punkt 17 · Vermarktung</p><h2>Statische 3D-Visualisierung</h2><p>Erzeugt auf Wunsch ein einzelnes KI-Bild auf Basis des vorhandenen Grundrisses. Es ist keine interaktive, vermessene oder geometrisch garantierte 3D-Anwendung.</p></section>
      <details className="panel-card"><summary>Bearbeitbares Grundrissmodell und Möbel</summary><p>Die KI-Bilder enthalten kein strukturiertes Modell. Lade hier ein vorhandenes Modell oder erfasse die Geometrie. Möbel verändern keine Wände, Türen oder Fenster.</p>{modelView ? <FloorPlan3D project={model} onChange={setModel} onBack={()=>setModelView(false)} onEdit2d={()=>setModelView(false)}/> : <FloorPlanEditor project={model} onChange={setModel} onOpen3d={()=>setModelView(true)}/>}</details>
      <section className="floorplan-layout">
        <div className="panel-card floorplan-controls"><div className="form-block"><button type="button" disabled={!cleanResult} onClick={() => void saveStudioProject()}>Grundrissprojekt speichern</button><label>Grundrissprojekt laden<input type="file" accept="application/json" onChange={event=>void loadStudioProject(event.target.files?.[0])}/></label></div>
          <div className="label-row"><strong>Ausgangsgrundlage</strong><span>{cleanResult ? 'Sauberer 2D-Entwurf' : 'Original'}</span></div>
          <p className="field-hint">{sourceFileName}</p>
          {!file && <div className="notice notice--info"><p>Öffne zuerst «2D-Grundriss», lade eine Vorlage hoch und erzeuge oder prüfe dort den Entwurf.</p></div>}
          {file && <><div className="plan-source-preview"><img src={workingImage} alt="Grundlage für die 3D-Visualisierung" /></div><p className="field-hint">Die 3D-Visualisierung startet erst durch deinen Klick. Sie beginnt ohne behauptete Messdaten.</p></>}
          <div className="form-block"><strong>Bodendarstellung</strong><p>Dezente Pastellflächen</p></div>
          <button className="primary-button" type="button" disabled={!canCreate3d} onClick={() => void create3d()}>{loading === '3d' ? '3D-Visualisierung wird erstellt …' : 'Möblierte 3D-Visualisierung erstellen'}</button>
          <button className="secondary-button" type="button" onClick={onOpen2d}>← Zur 2D-Aufbereitung</button>
          {error && <div className="notice notice--error" role="alert"><p>{error}</p></div>}
          {message && !error && <div className="notice notice--info" role="status"><p>{message}</p></div>}
          {!apiReady && <div className="notice notice--warning"><p>Für die KI-Visualisierung ist kein API-Schlüssel konfiguriert. Es wird kein Ergebnis erfunden.</p></div>}
        </div>
        <div className="panel-card floorplan-result">
          {threeDResult ? <><div className="floorplan-result-image"><img src={threeDResult.image} alt="Statische 3D-KI-Visualisierung des Grundrisses" /></div><p className="result-meta"><span>{threeDResult.promptSummary}</span><span>{threeDResult.result.width} × {threeDResult.result.height}px</span></p><p className="result-disclaimer">{threeDResult.note}</p>{threeDResult.correctedImage && threeDResult.distortion && <details className="floorplan-distortion-check"><summary>Analysierte Vorlage prüfen</summary><div className="floorplan-distortion-content"><img src={threeDResult.correctedImage} alt={threeDResult.distortion.corrected ? 'Korrigierte Arbeitsvorlage für die 3D-Visualisierung' : 'Analysierte Arbeitsvorlage für die 3D-Visualisierung'} /><div><strong>{threeDResult.distortion.summary}</strong>{threeDResult.distortion.details.map((detail) => <p className="field-hint" key={detail}>{detail}</p>)}</div></div></details>}<button className="secondary-button" type="button" onClick={() => void downloadFloorPlanResult(threeDResult, threeDResult.image, 'property-partners-grundriss-3d-visualisierung.png')}>PNG herunterladen</button></> : <div className="empty-result small-empty"><p className="eyebrow">Noch kein Ergebnis</p><h3>3D bleibt optional</h3><p>Prüfe zuerst den 2D-Entwurf. Danach kannst du bewusst eine statische 3D-KI-Visualisierung anfordern.</p></div>}
        </div>
      </section>
    </div>;
  }

  return <div aria-busy={orientationSaving} hidden={!active} className={`floorplan-area wrap ${file ? 'has-file' : ''}`} aria-label="2D-Grundriss"><fieldset disabled={orientationSaving} style={{display: 'contents'}}>
    <section className="area-heading"><p className="eyebrow">Roadmap-Punkt 16 · Vermarktung</p><h2>2D-Grundriss automatisch aufbereiten</h2><p>Lade einen vorhandenen Grundriss hoch und erzeuge per Klick eine saubere 2D-KI-Visualisierung. Du musst keine Wände oder Räume nachzeichnen.</p></section>
    <section className="floorplan-layout">
      <div className="panel-card floorplan-controls"><div className="form-block"><button type="button" disabled={!cleanResult} onClick={() => void saveStudioProject()}>Grundrissprojekt speichern</button><label>Grundrissprojekt laden<input type="file" accept="application/json" onChange={event=>void loadStudioProject(event.target.files?.[0])}/></label></div>
        <div className="dropzone floorplan-dropzone" onClick={() => document.getElementById('floorplan-file')?.click()} role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') document.getElementById('floorplan-file')?.click(); }}>
          <input id="floorplan-file" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden onChange={(event) => { handleFile(event.target.files?.[0]); event.currentTarget.value = ''; }} />
          {pdfFile && pdfPreview ? <div
            className={`floorplan-source-stage ${cropSelecting ? 'floorplan-source-stage--selecting' : ''}`}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={cropSelecting ? startCropSelection : undefined}
            onPointerMove={cropSelecting ? moveCropSelection : undefined}
            onPointerUp={cropSelecting ? finishCropSelection : undefined}
            onPointerCancel={cropSelecting ? finishCropSelection : undefined}
          ><img src={pdfPreview} alt={`PDF-Seite ${pdfPage} als Grundrissvorschau`} draggable="false" />{cropRegion && <div className="floorplan-crop-region" style={{ left: `${cropRegion.x * 100}%`, top: `${cropRegion.y * 100}%`, width: `${cropRegion.width * 100}%`, height: `${cropRegion.height * 100}%` }}><span>Gewählter Ausschnitt</span></div>}{cropDraft && cropSelecting && <div className="floorplan-crop-region floorplan-crop-region--draft" style={{ left: `${cropDraft.x * 100}%`, top: `${cropDraft.y * 100}%`, width: `${cropDraft.width * 100}%`, height: `${cropDraft.height * 100}%` }} />}</div>
          : file && originalPreview ? <div
            className={`floorplan-source-stage ${cropSelecting ? 'floorplan-source-stage--selecting' : ''}`}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={cropSelecting ? startCropSelection : undefined}
            onPointerMove={cropSelecting ? moveCropSelection : undefined}
            onPointerUp={cropSelecting ? finishCropSelection : undefined}
            onPointerCancel={cropSelecting ? finishCropSelection : undefined}
          >
            <img src={originalPreview} alt="Hochgeladener Originalgrundriss" draggable="false" />
            {cropRegion && <div className="floorplan-crop-region" style={{ left: `${cropRegion.x * 100}%`, top: `${cropRegion.y * 100}%`, width: `${cropRegion.width * 100}%`, height: `${cropRegion.height * 100}%` }}><span>Gewählter Ausschnitt</span></div>}
            {cropDraft && cropSelecting && <div className="floorplan-crop-region floorplan-crop-region--draft" style={{ left: `${cropDraft.x * 100}%`, top: `${cropDraft.y * 100}%`, width: `${cropDraft.width * 100}%`, height: `${cropDraft.height * 100}%` }} />}
          </div> : <><strong>Grundriss als Bild oder PDF hochladen</strong><span>JPG, PNG oder WebP · maximal 15 MB; PDF · maximal 20 MB</span></>}
        </div>
        {pdfFile && <div className="form-block pdf-import-panel"><div className="label-row"><strong>PDF-Grundriss auswählen</strong><span>{pdfPageCount} Seite{pdfPageCount === 1 ? '' : 'n'}</span></div><div className="pdf-page-thumbnails">{pdfThumbnails.map((thumbnail) => <button type="button" className={thumbnail.page === pdfPage ? 'is-selected' : ''} key={thumbnail.page} onClick={() => setPdfPage(thumbnail.page)}><img src={thumbnail.src} alt={`Seite ${thumbnail.page}`} /><span>Seite {thumbnail.page}</span></button>)}</div><label>Seitenausrichtung<select value={pdfRotation} onChange={(event) => { setPdfRotation(Number(event.target.value)); setCropRegion(null); setCropDraft(null); }}><option value="0">0°</option><option value="90">90° im Uhrzeigersinn</option><option value="180">180°</option><option value="270">270° im Uhrzeigersinn</option></select></label><div className="plan-actions plan-actions--inline"><button className={`secondary-button ${cropSelecting ? 'is-selected' : ''}`} type="button" disabled={pdfBusy} onClick={() => { setCropSelecting((current) => !current); setCropDraft(cropRegion); }}>{cropSelecting ? 'Ausschnitt markieren …' : cropRegion ? 'Ausschnitt ändern' : 'Optionalen Ausschnitt markieren'}</button>{cropRegion && <button className="text-danger-button" type="button" disabled={pdfBusy} onClick={() => { setCropRegion(null); setCropDraft(null); }}>Auswahl löschen</button>}</div><p className="field-hint">PDF-Limit: maximal 30 Seiten; Vorschau und Übernahme bis 2400 px Kantenlänge und 24 Megapixel. Die Seite wird lokal gerendert, externe Ressourcen werden nicht geladen.</p><div className="plan-actions plan-actions--inline"><button className="primary-button" type="button" disabled={pdfBusy || !pdfPreview} onClick={() => void acceptPdfImport()}>Grundriss übernehmen</button><button className="secondary-button" type="button" disabled={pdfBusy} onClick={cancelPdfImport}>Abbrechen</button></div></div>}
        {file && <div className="file-line"><span>{sourceFileName}</span><button type="button" onClick={() => rejectFile('Grundriss entfernt.')}>Entfernen</button></div>}
        {file && <div className="form-block floorplan-crop-controls">
          <div className="label-row"><strong>Gewünschten Grundriss auswählen</strong><span>{cropRegion ? '1 Ausschnitt markiert' : 'Optional'}</span></div>
          <p className="field-hint">Wenn die Vorlage mehrere Geschosse oder Pläne enthält, ziehe einen Rahmen um genau den vollständigen Ausschnitt, der aufbereitet werden soll. Die Auswahl ist kein Nachzeichnen.</p>
          <div className="plan-actions plan-actions--inline"><button className={`secondary-button ${cropSelecting ? 'is-selected' : ''}`} type="button" aria-pressed={cropSelecting} onClick={() => { setCropSelecting((current) => !current); setCropDraft(cropRegion); setError(''); }}>{cropSelecting ? 'Ausschnitt markieren …' : cropRegion ? 'Ausschnitt ändern' : 'Ausschnitt markieren'}</button>{cropRegion && <button className="text-danger-button" type="button" onClick={() => { setCropRegion(null); setCropDraft(null); setError(''); }}>Auswahl löschen</button>}</div>
          <small className="field-hint">Lass den Rahmen gross genug für alle Wände, Türen, Fenster und Treppen. Die Anwendung setzt danach einen kleinen gleichmässigen weissen Rand.</small>
        </div>}
        <div className="form-block"><strong>Bodendarstellung</strong><p>Dezente Pastellflächen</p></div>
        <div className="form-block"><div className="label-row"><strong>Automatische Aufbereitung</strong><span>kein Zeichnen nötig</span></div><p className="field-hint">Weisser Hintergrund, klare Wände und vorlagengetreue Türen, Fenster und Treppen. Planrahmen, technische Zusatztexte, Masse und Flächenangaben werden nicht übernommen.</p><button className="primary-button" type="button" disabled={Boolean(loading) || pdfBusy} onClick={() => void create2d()}>{loading === '2d' ? '2D-Entwurf wird erstellt …' : cleanResult ? 'Sauberen 2D-Entwurf neu erzeugen' : 'Sauberen 2D-Grundriss erzeugen'}</button></div>
        <div ref={startFeedback} aria-live="polite">{error && <div className="notice notice--error" role="alert"><p>{error}</p></div>}
        {message && !error && <div className="notice notice--info" role="status"><p>{message}</p></div>}</div>
        {cleanResult && <div className="form-block"><button type="button" className="secondary-button" aria-pressed={!localEdit} onClick={() => setLocalEdit(false)}>Gesamten Grundriss korrigieren</button><button type="button" className="secondary-button" aria-pressed={localEdit} onClick={() => {setLocalEdit(true); setPreviewMode('result');}}>Bereich korrigieren</button>{localEdit && <><p>Rechteck im aktuellen Ergebnis aufziehen. Ausserhalb bleiben die bisherigen Pixel erhalten. Bei abweichenden Bildabmessungen wird abgebrochen.</p>{editRegion && <>{(['x','y','width','height'] as const).map(key => <label key={key}>{key} (relativ)<input type="number" min="0" max="1" step="0.01" value={editRegion[key]} onChange={event => setEditRegion({...editRegion, [key]: Number(event.target.value)})}/></label>)}<button type="button" onClick={() => setEditRegion(null)}>Auswahl entfernen</button></>}</>}{previousResult && <button type="button" onClick={() => {setCleanResult(previousResult);setPreviousResult(cleanResult);setEditRegion(null);}}>Vorherigen Ergebnisstand wiederherstellen</button>}</div>}
        {cleanResult && <div className="form-block"><div className="label-row"><label htmlFor="floorplan-correction">Gezielte Korrekturanweisung</label><span>{correction.length} / {MAX_FLOORPLAN_CORRECTION_LENGTH}</span></div><textarea id="floorplan-correction" rows={4} maxLength={MAX_FLOORPLAN_CORRECTION_LENGTH} value={correction} onChange={(event) => setCorrection(event.target.value)} placeholder="z. B. Die Tür zum Schlafzimmer fehlt. Bitte anhand des Originals korrigieren." /><p className="field-hint">Bei einer Korrektur werden der aktuelle Entwurf und das Original als Referenzen übergeben, soweit der Provider dies unterstützt.</p><button className="secondary-button" type="button" disabled={!correction.trim() || Boolean(loading) || (localEdit && (!editRegion || editRegion.width <= 0 || editRegion.height <= 0 || editRegion.x + editRegion.width > 1 || editRegion.y + editRegion.height > 1))} onClick={() => void create2d()}>2D-Entwurf korrigieren</button></div>}
        <div className="form-block"><div className="label-row"><strong>Optionale Raumbeschriftungen</strong><span>{labelsDescription}</span></div><p className="field-hint">Beschriftungen werden separat über das Bild gelegt und lösen keine neue Generierung aus. Eigene Flächen bleiben ausdrücklich Nutzerangaben.</p><button className={`secondary-button ${addingLabel ? 'is-selected' : ''}`} type="button" disabled={!workingImage} onClick={() => { if (cleanResult) setPreviewMode('result'); setAddingLabel((current) => !current); setNewLabelPosition(null); }}>+ Beschriftung durch Klick auf das Bild hinzufügen</button>{workingImage && <div className="label-stage-help">{addingLabel ? 'Klicke im Ergebnisbild auf die gewünschte Position.' : 'Zum Verschieben eine Beschriftung direkt ziehen.'}</div>}</div>
        {workingImage && <div className="label-settings form-block"><div className="label-row"><strong>Beschriftungsstil</strong><span>{labels.length > 0 ? 'gilt für alle' : 'für neue Beschriftungen'}</span></div><label>Namensgrösse <output>{Math.round(labelStyle.nameSizeRatio * 1000) / 10}%</output><input type="range" min="0.01" max="0.04" step="0.001" value={labelStyle.nameSizeRatio} onChange={(event) => setLabelStyle((current) => ({ ...current, nameSizeRatio: Number(event.target.value) }))} /></label><label>Namensgewicht <select value={labelStyle.nameWeight} onChange={(event) => setLabelStyle((current) => ({ ...current, nameWeight: Number(event.target.value) === 500 ? 500 : 400 }))}><option value="400">Normal · 400</option><option value="500">Mittel · 500</option></select></label><label>Namensfarbe <input type="color" value={labelStyle.nameColour} onChange={(event) => setLabelStyle((current) => ({ ...current, nameColour: event.target.value }))} /></label><label>Flächenfarbe <input type="color" value={labelStyle.areaColour} onChange={(event) => setLabelStyle((current) => ({ ...current, areaColour: event.target.value }))} /></label><div className="plan-actions plan-actions--inline"><button className="secondary-button" type="button" onClick={applyDefaultLabelStyle}>Standardgestaltung anwenden</button><button className="secondary-button" type="button" onClick={saveLabelDocument}>Beschriftungen speichern</button><button className="secondary-button" type="button" onClick={() => labelDocumentInputRef.current?.click()}>Beschriftungen laden</button><input ref={labelDocumentInputRef} type="file" accept="application/json" hidden onChange={(event) => void loadLabelDocument(event.target.files?.[0])} /></div>{labels.map((label) => <div className="floorplan-label-row" key={label.id}><input aria-label="Raumname" value={label.name} onChange={(event) => updateLabel(label.id, { name: event.target.value })} /><input aria-label="Eigene ungefähre Fläche" placeholder="ca. 12 m²" value={label.area} onChange={(event) => updateLabel(label.id, { area: event.target.value })} onBlur={() => updateLabel(label.id, { area: normalizeFloorPlanArea(label.area) })} /><button type="button" aria-label={`${label.name || 'Beschriftung'} löschen`} onClick={() => setLabels((current) => current.filter((item) => item.id !== label.id))}>×</button><label className="floorplan-label-rotation">Drehung <output>{normalizeFloorPlanRotation(label.rotation)}°</output><input type="range" min="-180" max="180" step="1" value={normalizeFloorPlanRotation(label.rotation)} aria-label={`${label.name || 'Beschriftung'} drehen`} onChange={(event) => updateLabel(label.id, { rotation: normalizeFloorPlanRotation(Number(event.target.value)) })} /></label></div>)}</div>}
          {workingImage && <div className="plan-actions"><button className="secondary-button" type="button" onClick={() => void downloadFloorPlanResult(cleanResult, workingImage, `property-partners-grundriss-2d-ohne-beschriftung${cleanResult?.processingVersion ? `-${cleanResult.processingVersion}` : ''}.png`)}>{cleanResult?.downloadFilename?.endsWith('.webp') ? '2D-WebP ohne Beschriftung' : '2D-PNG ohne Beschriftung'}</button><button className="secondary-button" type="button" onClick={() => void downloadLabels()}>2D-PNG mit Beschriftung</button><button className="secondary-button" type="button" onClick={onOpen3d}>Weiter zur 3D-Visualisierung</button></div>}

        {!apiReady && <div className="notice notice--warning"><p>Für die automatische Grundrissaufbereitung ist kein API-Schlüssel konfiguriert. Es wird kein Ergebnis erfunden.</p></div>}
      </div>
      <div className="panel-card floorplan-result">
        {cleanResult && <>
          {cleanResult.versionId && <div className="plan-actions"><button type="button" className="secondary-button" disabled={orientationSaving || Boolean(loading)} onClick={() => void rotateResult()}>{orientationSaving ? 'Ausrichtung wird gespeichert …' : orientationOperation.current?.versionId === cleanResult.versionId ? 'Drehung erneut speichern' : '90° drehen'}</button><span>Aktuelle Ausrichtung: {cleanResult.degrees || 0}°</span><span className="rotation-feedback" role={orientationError ? 'alert' : 'status'}>{orientationSaving ? 'Speichern …' : orientationError ? orientationError + ' Bitte bewusst erneut speichern.' : cleanResult.revision ? 'Ausrichtung gespeichert.' : ''}</span></div>}
          <p className="result-meta"><span>{cleanResult.promptSummary}</span><span>Original {cleanResult.original.width} × {cleanResult.original.height}px · Entwurf {cleanResult.result.width} × {cleanResult.result.height}px{cleanResult.processingVersion ? ` · ${cleanResult.processingVersion}` : ''}</span></p>
          <p className="result-disclaimer">{cleanResult.note} Der Vergleich verwendet eine zuverlässige Ansicht nebeneinander; die Bilder werden nicht aufeinandergelegt oder gestreckt.</p>
          {cleanResult.correctedImage && cleanResult.distortion && <details className="floorplan-distortion-check" open={cleanResult.distortion.reviewRequired || cleanResult.distortion.corrected}>
            <summary>Vorlage und mögliche Entzerrung prüfen</summary>
            <div className="floorplan-distortion-content">
              <img src={cleanResult.correctedImage} alt={cleanResult.distortion.corrected ? 'Korrigierte Arbeitsvorlage vor der Grundrissaufbereitung' : 'Analysierte Arbeitsvorlage vor der Grundrissaufbereitung'} />
              <div><strong>{cleanResult.distortion.summary}</strong><p className="field-hint">Die vollständige Arbeitsvorlage wurde vor Zuschnitt und Neuzeichnung analysiert. Das hochgeladene Original bleibt unverändert. {cleanResult.distortion.reviewRequired ? 'Bitte prüfe insbesondere Falten, Randlinien, echte schräge Wände und Übergänge.' : ''}</p>{cleanResult.distortion.details.map((detail) => <p className="field-hint" key={detail}>{detail}</p>)}</div>
            </div>
          </details>}
          {cropRegion && originalPreview && <a className="secondary-button floorplan-full-original-link" href={originalPreview} target="_blank" rel="noreferrer">Vollständiges Original öffnen</a>}
        </>}
        {workingImage && <>
          <div className="floorplan-preview-tabs" role="tablist" aria-label="Grundrissvorschau">
            <button type="button" role="tab" aria-selected={previewMode === 'result'} disabled={!cleanResult || orientationSaving || Boolean(loading)} className={previewMode === 'result' ? 'is-selected' : ''} onClick={() => selectPreviewMode('result')}>Ergebnis</button>
            <button type="button" role="tab" aria-selected={previewMode === 'original'} className={previewMode === 'original' ? 'is-selected' : ''} onClick={() => selectPreviewMode('original')}>Original</button>
            <button type="button" role="tab" aria-selected={previewMode === 'compare'} disabled={!canShowComparison} className={previewMode === 'compare' ? 'is-selected' : ''} onClick={() => selectPreviewMode('compare')}>Vergleich</button>
          </div>
          {previewMode === 'compare' && cleanResult ? <div ref={labelStageRef} className="floorplan-label-stage floorplan-label-stage--compare">
            <div className="floorplan-compare-view">
              <div className="floorplan-compare-pane"><img src={cleanResult.originalImage} alt="Originalgrundriss" /><span>Original</span></div>
              <div className="floorplan-compare-pane"><img src={cleanResult.image} alt="KI-Entwurf des Grundrisses" /><span>KI-Entwurf · {cleanResult.degrees || 0}°</span></div>
            </div>
          </div> : <>
            <div className="floorplan-preview-toolbar" aria-label="Vorschau-Steuerung"><button className="secondary-button" type="button" onClick={() => setPreviewZoomValue(previewZoom - 0.25)} disabled={previewZoom <= 1} aria-label="Vorschau verkleinern">−</button><span>{Math.round(previewZoom * 100)}%</span><button className="secondary-button" type="button" onClick={() => setPreviewZoomValue(previewZoom + 0.25)} disabled={previewZoom >= 3} aria-label="Vorschau vergrössern">+</button><button className="secondary-button" type="button" onClick={resetPreviewView}>Ganzes Bild</button></div>
            <div ref={labelStageRef} className={`floorplan-label-stage ${previewZoom > 1 ? 'is-zoomed' : ''} ${draggingPreview ? 'is-panning' : ''}`} onClick={handleStageClick} onPointerDown={startPreviewPan} onPointerMove={handleStagePointerMove} onPointerUp={stopDragging} onPointerCancel={(event) => { if (editStart.current) setEditRegion(null); stopDragging(event); }}>
              <div className="floorplan-image-content" style={{ left: `${previewImageRect.left}px`, top: `${previewImageRect.top}px`, width: `${previewImageRect.width}px`, height: `${previewImageRect.height}px`, transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})` }}>
                <img draggable={false} onError={() => setError('Das Vorschaubild konnte nicht geladen werden. Bitte öffne die Ansicht erneut oder lade das gespeicherte Projekt neu.')} src={previewImage} alt={previewMode === 'result' && cleanResult ? 'Grundrissentwurf mit optionaler Beschriftungsebene' : 'Originalgrundriss'} onLoad={(event) => { setLabelImageDimensions({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight }); setPreviewLoadedSource(previewImage); }} />
                {localEdit && previewMode === 'result' && editRegion && <div className="floorplan-crop-region" data-region-action="move" style={{cursor: 'move', left: `${editRegion.x*100}%`, top: `${editRegion.y*100}%`, width: `${editRegion.width*100}%`, height: `${editRegion.height*100}%`}}><span data-region-action="resize" aria-label="Bereichsgrösse ändern" style={{position:"absolute",right:0,bottom:0,width:22,height:22,background:"#AE7839",cursor:"nwse-resize",pointerEvents:"auto"}} /></div>}
                {(previewMode === 'result' || !cleanResult) && labels.map((label) => <button key={label.id} type="button" className="floorplan-label" style={{ left: `${label.x * 100}%`, top: `${label.y * 100}%`, color: labelStyle.nameColour, fontSize: labelPreviewFontSize ? `${labelPreviewFontSize}px` : undefined, fontWeight: labelStyle.nameWeight, transform: `translate(-50%, -50%) rotate(${normalizeFloorPlanRotation(label.rotation)}deg)` }} onPointerDown={(event) => handleLabelPointerDown(event, label.id)}><span className="floorplan-label-name">{label.name}</span>{label.area && <small style={{ color: labelStyle.areaColour }}>{normalizeFloorPlanArea(label.area)}</small>}</button>)}
                {(previewMode === 'result' || !cleanResult) && newLabelPosition && <div className="floorplan-label floorplan-label--draft" style={{ left: `${newLabelPosition.x * 100}%`, top: `${newLabelPosition.y * 100}%`, fontSize: labelPreviewFontSize ? `${labelPreviewFontSize}px` : undefined }}>Neue Beschriftung</div>}
              </div>
            </div>
            {previewZoom > 1 && <p className="field-hint">Verschiebe die vergrösserte Vorschau mit gedrückter Maustaste oder per Touch.</p>}
          </>}
        </>}
        {!workingImage && <div className="empty-result small-empty"><p className="eyebrow">Vorschau</p><h3>Noch kein Grundriss</h3><p>Lade einen Plan hoch. Das Ergebnis erscheint nach deinem bewussten Start separat vom Original.</p></div>}
        {newLabelPosition && <div className="label-editor"><strong>Neue Raumbeschriftung</strong><label>Raumname<input autoFocus value={newLabelName} onChange={(event) => setNewLabelName(event.target.value)} placeholder="z. B. Schlafzimmer" /></label><label>Eigene ungefähre Fläche (optional)<input value={newLabelArea} onChange={(event) => setNewLabelArea(event.target.value)} onBlur={() => setNewLabelArea(normalizeFloorPlanArea(newLabelArea))} placeholder="z. B. ca. 12 m²" /></label><div className="plan-actions"><button className="primary-button" type="button" onClick={saveNewLabel}>Beschriftung speichern</button><button className="secondary-button" type="button" onClick={() => setNewLabelPosition(null)}>Abbrechen</button></div></div>}
      </div>
    </section>
  </fieldset></div>;
}
